import type { ReactNode } from "react";
import { Link } from "react-router-dom";

/** Semantic tone for a marketplace status (application / bid / posting). */
function tone(status: string): string {
  const s = status.toLowerCase();
  if (/accept|award|shortlist|offer|verified|active/.test(s)) return "text-verified";
  // Caution / awaiting-action states get the warn token (added Phase 3).
  if (/hold|held|under.?review|needs.?info|processing|escrow/.test(s)) return "text-warn";
  if (/reject|decline|not selected|fail|clawed|refund/.test(s)) return "text-danger";
  if (/appl|submit|review|view|open|pending/.test(s)) return "text-info";
  return "text-muted";
}

/** Monospace uppercase status label in its semantic color. */
export function StatusLabel({ status }: { status: string }) {
  return <span className={`font-mono text-xs font-bold uppercase tracking-wide ${tone(status)}`}>{status.replace(/_/g, " ")}</span>;
}

/** A bordered list card with a 3-column header (title / status / right). */
export function ListTable({ columns, children }: { columns: [string, string, string]; children: ReactNode }) {
  return (
    <div className="overflow-hidden rounded-card border border-line bg-surface">
      <div className="grid grid-cols-[1fr_auto] items-center gap-4 border-b border-line px-5 py-3 text-[11px] font-bold uppercase tracking-widest text-muted sm:grid-cols-[1fr_150px_120px]">
        <span>{columns[0]}</span>
        <span className="hidden sm:block">{columns[1]}</span>
        <span className="justify-self-end">{columns[2]}</span>
      </div>
      {children}
    </div>
  );
}

/** A row aligned to ListTable's columns; links to `to` or fires `onClick`. */
export function ListRow({
  to, onClick, title, meta, status, right,
}: {
  to?: string;
  onClick?: () => void;
  title: string;
  meta?: ReactNode;
  status?: string;
  right?: ReactNode;
}) {
  const cls =
    "grid w-full grid-cols-[1fr_auto] items-center gap-4 border-t border-line px-5 py-4 text-left transition-colors hover:bg-surface-2 sm:grid-cols-[1fr_150px_120px]";
  const inner = (
    <>
      <div className="grid min-w-0 gap-0.5">
        <span className="truncate font-display text-base font-black tracking-tight">{title}</span>
        {meta && <span className="truncate text-sm text-muted">{meta}</span>}
      </div>
      <div className="hidden sm:block">{status && <StatusLabel status={status} />}</div>
      <div className="justify-self-end font-mono text-xs text-muted">{right}</div>
    </>
  );
  return onClick ? (
    <button type="button" onClick={onClick} className={cls}>{inner}</button>
  ) : (
    <Link to={to ?? "#"} className={cls}>{inner}</Link>
  );
}
